/**
 * Mural de recados. INTERNO = aviso para a equipe (recesso, mudança de rotina, lembrete geral); CLIENTES = aviso que
 * aparece no topo do portal do cliente (todos ou só os escolhidos) e, se o escritório quiser, vai também por e-mail aos
 * contatos que recebem avisos. Cada um marca "lido"; o escritório vê quantos já viram.
 */
import { z } from 'zod';
import { DataISO, Id } from './comum.js';

export const PublicoRecado = z.enum(['INTERNO', 'CLIENTES']);
export type PublicoRecado = z.infer<typeof PublicoRecado>;

export const Recado = z.object({
  id: Id,
  publico: PublicoRecado,
  titulo: z.string(),
  texto: z.string(),
  importante: z.boolean(),
  fixado: z.boolean(),
  /** só CLIENTES: todos os clientes ativos ou os listados em `clientes` */
  todosClientes: z.boolean(),
  clientes: z.array(z.object({ id: Id, nome: z.string() })),
  /** depois desta data o recado sai do mural e do portal (continua no histórico) */
  expiraEm: DataISO.nullable(),
  expirado: z.boolean(),
  autorNome: z.string().nullable(),
  criadoEm: z.string(),
  atualizadoEm: z.string(),
  /** eu já li (só faz sentido no INTERNO) */
  lido: z.boolean(),
  /** quantos já viram: membros (INTERNO) ou contatos de clientes (CLIENTES) */
  leituras: z.number().int(),
  emailEnviadoEm: z.string().nullable(),
  podeEditar: z.boolean(),
});
export type Recado = z.infer<typeof Recado>;

export const RecadoBody = z.object({
  publico: PublicoRecado.default('INTERNO'),
  titulo: z.string().trim().min(3).max(120),
  texto: z.string().trim().min(1).max(4000),
  importante: z.boolean().default(false),
  fixado: z.boolean().default(false),
  todosClientes: z.boolean().default(true),
  clienteIds: z.array(Id).max(2000).default([]),
  expiraEm: DataISO.nullable().default(null),
  /** só CLIENTES: manda agora um e-mail aos contatos que recebem avisos */
  avisarPorEmail: z.boolean().default(false),
});
export type RecadoBody = z.infer<typeof RecadoBody>;

/** PATCH: só o que vier muda. O público não muda depois de criado (quem já leu, leu como interno ou como cliente). */
export const RecadoEditarBody = z.object({
  titulo: z.string().trim().min(3).max(120).optional(),
  texto: z.string().trim().min(1).max(4000).optional(),
  importante: z.boolean().optional(),
  fixado: z.boolean().optional(),
  todosClientes: z.boolean().optional(),
  clienteIds: z.array(Id).max(2000).optional(),
  expiraEm: DataISO.nullable().optional(),
});
export type RecadoEditarBody = z.infer<typeof RecadoEditarBody>;

export const MuralQuery = z.object({ publico: PublicoRecado.optional(), expirados: z.enum(['0', '1']).optional() });
export const MuralResumo = z.object({ naoLidos: z.number().int() });
export type MuralResumo = z.infer<typeof MuralResumo>;
export const RecadoCriado = z.object({ recado: Recado, /** e-mails postos na fila (0 quando não pediu ou não há contato) */ emails: z.number().int() });
export type RecadoCriado = z.infer<typeof RecadoCriado>;

/** O que o cliente vê no portal. */
export const PortalRecado = z.object({ id: Id, titulo: z.string(), texto: z.string(), importante: z.boolean(), criadoEm: z.string(), lido: z.boolean() });
export type PortalRecado = z.infer<typeof PortalRecado>;
